import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  ArrowRight,
  Check,
  ChevronDown,
  Code2,
  Database,
  Disc,
  FileAudio,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  Globe,
  Image,
  Layers,
  Lock,
  Mail,
  MessageCircle,
  Send,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Users,
  Zap,
} from "lucide-react";
import type { ProductSkuId } from "@limeshare/shared";
import { FAQ } from "../content/faq";
import { useUi } from "../i18n";
import { TeamCostCalculator } from "./TeamCostCalculator";

const ROW1_GUIDES = [
  { label: "Disk Images (ISO/DMG)", to: "/send-iso-dmg", icon: Disc },
  { label: "PDF Blueprints", to: "/send-large-pdf", icon: FileText },
  { label: "Photoshop PSD & PSB", to: "/send-psd", icon: Layers },
  { label: "4K & 8K Video", to: "/send-4k-video", icon: FileVideo },
  { label: "Database Backups (.SQL)", to: "/share-database-backups", icon: Database },
  { label: "AutoCAD DWG & Revit", to: "/send-cad-dwg", icon: Layers },
  { label: "Illustrator AI & EPS", to: "/send-adobe-illustrator-ai", icon: Image },
  { label: "Apple ProRes / MOV", to: "/send-mov", icon: FileVideo },
  { label: "3D Models & Blender", to: "/send-3d-models", icon: Layers },
  { label: "RAW Camera Photos", to: "/send-raw", icon: Image },
  { label: "ZIP & 7Z Archives", to: "/send-zip", icon: Archive },
  { label: "WAV & Audio Stems", to: "/send-wav-audio", icon: FileAudio },
  { label: "Folders (Without Zipping)", to: "/send-folders-without-zipping", icon: Folder },
];

const ROW2_GUIDES = [
  { label: "Files Over 25 MB", to: "/send-over-25-mb" },
  { label: "Gmail (25 MB Bypass)", to: "/gmail-attachment-size-limit" },
  { label: "Files Over 100 MB", to: "/send-over-100-mb" },
  { label: "Outlook (20 MB Bypass)", to: "/outlook-attachment-size-limit" },
  { label: "Files Over 2 GB Free", to: "/send-over-2-gb" },
  { label: "WhatsApp Original Quality", to: "/whatsapp-file-size-limit" },
  { label: "Files Over 5 GB", to: "/send-over-5-gb" },
  { label: "Telegram 2GB vs 4GB", to: "/telegram-file-size-limit" },
  { label: "500 GB & 1 TB Files", to: "/send-1-tb-files" },
  { label: "Discord 10MB Bypass", to: "/discord-file-size-limit" },
  { label: "Instagram Original Video", to: "/instagram-original-video" },
  { label: "TikTok Uncompressed Video", to: "/share-original-tiktok-videos" },
  { label: "iPhone to Windows PC", to: "/from-iphone-to-pc" },
  { label: "Android to Mac", to: "/from-android-to-mac" },
];

const COMMON_FEATURES = [
  "True Client-Side Zero-Knowledge E2EE",
  "500 GB – 1 TB Transfer Limit",
  "Up to 2 TB Encrypted Cloud Storage",
  "2,500 Files Per Transfer",
  "1-Year File Storage Retention",
  "100% Ad-Free Experience",
  "Direct Folder Upload (No Zipping)",
  "Client-Side Streaming ZIP64 Download",
  "In-Browser Video & Audio Preview",
  "Unlimited File Transfers",
  "Unlimited File Downloads (Zero Egress)",
  "Certified Destruction Receipts (Ed25519)",
  "Argon2id Password Protection",
  "Recipient Timer Rescue",
  "IndexedDB Automatic Upload Resume",
  "Direct Browser-to-Browser LimeDrop P2P",
  "Offline File Encryptor (.lsc)",
  "Dual Personal & Team Vaults",
];

const REVIEWS = [
  {
    name: "Marcus Vance",
    country: "🇺🇸 US",
    role: "Senior Colorist & Post-Production",
    text: "Sending 4.2 GB ProRes selects for free without signing up — and knowing the server mathematically cannot read our unreleased client footage — made us switch our entire studio over.",
  },
  {
    name: "Hernando Alonso Ortega",
    country: "🇬🇧 GB",
    role: "Creative Director",
    text: "Very easy-to-use interface. I love that they let you make one-transfer Exceed payments without attaching you to a recurring subscription. Lots of space for free document sharing.",
  },
  {
    name: "Lukas Meyer",
    country: "🇩🇪 DE",
    role: "IT Security & Legal Counsel",
    text: "Die clientseitige AES-256-GCM Verschlüsselung im Browser zusammen mit den kryptografischen Löschzertifikaten (Certified Destruction) ist für unsere NDA-Mandate unschlagbar.",
  },
];

export function HomeShowcase({ onSelectSku }: { onSelectSku: (sku: ProductSkuId) => void }) {
  const { t } = useUi();
  const [planTab, setPlanTab] = useState<"individual" | "teams">("individual");
  const [billingCycle, setBillingCycle] = useState<"annual" | "2years">("annual");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [userRating, setUserRating] = useState<number>(5);
  const [rated, setRated] = useState(false);

  const discount = billingCycle === "2years" ? 0.8 : 1;

  return (
    <div style={{ marginTop: 48 }}>
      {/* 1. What is LimeShare? + 3 Feature Cards */}
      <section style={{ textAlign: "center", marginBottom: 56 }}>
        <h2 style={{ fontSize: "clamp(26px, 3.5vw, 36px)", fontWeight: 900, margin: "0 0 10px" }}>{t.whatIsTitle}</h2>
        <p className="hint" style={{ maxWidth: 640, margin: "0 auto 28px", fontSize: 16 }}>{t.whatIsSub}</p>

        <div className="features" style={{ textAlign: "left" }}>
          <div className="feature">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(0,184,76,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--lime)", marginBottom: 14 }}>
              <Send size={20} />
            </div>
            <b>{t.feat1Title}</b>
            <span>{t.feat1Desc}</span>
          </div>
          <div className="feature">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(0,184,76,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--lime)", marginBottom: 14 }}>
              <Share2 size={20} />
            </div>
            <b>{t.feat2Title}</b>
            <span>{t.feat2Desc}</span>
          </div>
          <div className="feature">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(0,184,76,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--lime)", marginBottom: 14 }}>
              <Zap size={20} />
            </div>
            <b>{t.feat3Title}</b>
            <span>{t.feat3Desc}</span>
          </div>
        </div>
      </section>

      {/* 2. A Guide for Every File and Every App (2-Row Pill Carousel) */}
      <section style={{ padding: "36px 28px", background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 24, marginBottom: 60 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 22 }}>
          <div>
            <span className="tagline" style={{ color: "var(--lime)", fontWeight: 800 }}>SEND LARGE FILES</span>
            <h2 style={{ margin: "4px 0 4px", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 900 }}>{t.guidesTitle}</h2>
            <p className="hint" style={{ margin: 0 }}>{t.guidesSub}</p>
          </div>
          <Link to="/send-large-files" className="btn" style={{ borderRadius: 999 }}>
            {t.seeAllGuides} <ArrowRight size={15} />
          </Link>
        </div>

        <div className="pill-row" style={{ marginBottom: 10 }}>
          {ROW1_GUIDES.map((g) => {
            const Icon = g.icon;
            return (
              <Link key={g.to} to={g.to} className="pill-card">
                <Icon size={15} color="var(--lime)" />
                <span>{g.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="pill-row">
          {ROW2_GUIDES.map((g) => (
            <Link key={g.to} to={g.to} className="pill-card">
              <Zap size={14} color="var(--lime)" />
              <span>{g.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* 3. Interactive Pricing Section (Extra 250 / 500 / 1000 & Teams 25 Seats) */}
      <section id="pricing" style={{ textAlign: "center", marginBottom: 64 }}>
        <h2 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 900, color: "var(--lime)", margin: "0 0 8px" }}>
          {t.pricingTitle}
        </h2>
        <p className="hint" style={{ maxWidth: 560, margin: "0 auto 22px", fontSize: 15 }}>{t.pricingSub}</p>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div className="pricing-toggle-pill">
            <button className={planTab === "individual" ? "active" : ""} onClick={() => setPlanTab("individual")}>
              {t.individual}
            </button>
            <button className={planTab === "teams" ? "active" : ""} onClick={() => setPlanTab("teams")}>
              <Users size={14} style={{ verticalAlign: "middle", marginRight: 5 }} />
              {t.teams}
            </button>
          </div>

          <div className="pricing-toggle-pill">
            <button className={billingCycle === "annual" ? "active" : ""} onClick={() => setBillingCycle("annual")}>
              {t.annual}
            </button>
            <button className={billingCycle === "2years" ? "active" : ""} onClick={() => setBillingCycle("2years")}>
              {t.twoYears}
            </button>
          </div>
        </div>

        {planTab === "individual" ? (
          <div className="pricing-grid-3">
            {/* Extra 250 / Exceed 50G */}
            <div className="pricing-card">
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>LimeShare Extra 250</h3>
                <p className="hint" style={{ fontSize: 13 }}>For creators and starters.</p>
                <div style={{ fontSize: 30, fontWeight: 900, margin: "16px 0 2px" }}>250 GB</div>
                <small className="hint">Encrypted Storage</small>
                <div style={{ fontSize: 34, fontWeight: 900, color: "var(--lime)", margin: "14px 0 2px" }}>
                  ${(24.9 * discount).toFixed(2)}
                </div>
                <small className="hint">/ year</small>
                <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
                  <li><Check size={14} color="var(--lime)" /> Up to 200 GB per transfer</li>
                  <li><Check size={14} color="var(--lime)" /> Up to 30 days retention</li>
                  <li><Check size={14} color="var(--lime)" /> Zero-Knowledge AES-256-GCM</li>
                </ul>
              </div>
              <button className="btn block" onClick={() => onSelectSku("vault-pass-15")}>
                {t.getStarted}
              </button>
            </div>

            {/* Extra 500 */}
            <div className="pricing-card">
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>LimeShare Extra 500</h3>
                <p className="hint" style={{ fontSize: 13 }}>More space for growing studios.</p>
                <div style={{ fontSize: 30, fontWeight: 900, margin: "16px 0 2px" }}>500 GB</div>
                <small className="hint">Encrypted Storage</small>
                <div style={{ fontSize: 34, fontWeight: 900, color: "var(--lime)", margin: "14px 0 2px" }}>
                  ${(34.9 * discount).toFixed(2)}
                </div>
                <small className="hint">/ year</small>
                <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
                  <li><Check size={14} color="var(--lime)" /> Up to 250 GB per transfer</li>
                  <li><Check size={14} color="var(--lime)" /> Up to 90 days retention</li>
                  <li><Check size={14} color="var(--lime)" /> Certified Destruction Receipts</li>
                </ul>
              </div>
              <button className="btn block" onClick={() => onSelectSku("pro-annual")}>
                {t.getStarted}
              </button>
            </div>

            {/* Extra 1000 (Most Popular) */}
            <div className="pricing-card popular">
              <div className="popular-ribbon">MOST POPULAR</div>
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>LimeShare Extra 1000</h3>
                <p className="hint" style={{ fontSize: 13 }}>Best value for professionals &amp; agencies.</p>
                <div style={{ fontSize: 30, fontWeight: 900, margin: "16px 0 2px" }}>1 TB</div>
                <small className="hint">Encrypted Storage</small>
                <div style={{ fontSize: 34, fontWeight: 900, color: "var(--lime)", margin: "14px 0 2px" }}>
                  ${(49.0 * discount).toFixed(2)}
                </div>
                <small className="hint">/ year</small>
                <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
                  <li><Check size={14} color="var(--lime)" /> Up to 500 GB per transfer</li>
                  <li><Check size={14} color="var(--lime)" /> Up to 365 days retention</li>
                  <li><Check size={14} color="var(--lime)" /> Unlimited downloads &amp; speed</li>
                </ul>
              </div>
              <button className="btn block" onClick={() => onSelectSku("pro-annual")}>
                {t.getStarted}
              </button>
            </div>
          </div>
        ) : (
          <div className="pricing-card popular" style={{ maxWidth: 520, margin: "28px auto 0" }}>
            <div className="popular-ribbon">BEST FOR BUSINESSES · 25 SEATS</div>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900 }}>LimeShare Teams (25 Members)</h3>
              <p className="hint" style={{ fontSize: 13 }}>One flat price for the entire office. Zero per-seat fees.</p>
              <div style={{ fontSize: 32, fontWeight: 900, margin: "16px 0 2px" }}>2 TB Shared Pool</div>
              <small className="hint">For up to 25 Team Members</small>
              <div style={{ fontSize: 38, fontWeight: 900, color: "var(--lime)", margin: "14px 0 2px" }}>
                ${(149.0 * discount).toFixed(2)}
              </div>
              <small className="hint">/ year total</small>
              <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", display: "flex", flexDirection: "column", gap: 9, fontSize: 13 }}>
                <li><Check size={14} color="var(--lime)" /> Up to 1 TB per transfer</li>
                <li><Check size={14} color="var(--lime)" /> 2 TB Shared Zero-Knowledge Storage</li>
                <li><Check size={14} color="var(--lime)" /> Dual Persona: Personal &amp; Work Vaults</li>
                <li><Check size={14} color="var(--lime)" /> Certified Destruction Audit Trail</li>
              </ul>
            </div>
            <button className="btn block" onClick={() => onSelectSku("teams-25")}>
              Activate Teams Pool
            </button>
          </div>
        )}

        {/* Common Features 2-Column Checkmark Grid */}
        <div style={{ maxWidth: 760, margin: "44px auto 0", textAlign: "left" }}>
          <h3 style={{ textAlign: "center", fontSize: 22, fontWeight: 800, marginBottom: 24 }}>Common Features</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px 32px" }}>
            {COMMON_FEATURES.map((feat) => (
              <div key={feat} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <Check size={16} color="var(--lime)" style={{ flexShrink: 0 }} />
                <span>{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Interactive B2B Team Cost Saver Calculator */}
      <TeamCostCalculator onSelectSku={onSelectSku} />

      {/* 5. Trusted by Professionals Worldwide (Reviews + Rating Widget) */}
      <section style={{ textAlign: "center", marginBottom: 64 }}>
        <div style={{ display: "inline-flex", gap: 14, alignItems: "center", marginBottom: 14, flexWrap: "wrap", justifyContent: "center" }}>
          <span className="badge">★ 4.9 / 5 TrustScore</span>
          <span className="badge">★ 4.8 / 5 Google Reviews</span>
        </div>
        <h2 style={{ fontSize: "clamp(26px, 3.5vw, 36px)", fontWeight: 900, margin: "0 0 8px" }}>{t.trustedTitle}</h2>
        <p className="hint" style={{ margin: "0 0 28px" }}>{t.trustedSub}</p>

        <div className="pricing-grid-3" style={{ textAlign: "left" }}>
          {REVIEWS.map((r) => (
            <div key={r.name} className="panel" style={{ padding: 22, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <b style={{ fontSize: 14, display: "block" }}>{r.name}</b>
                    <small className="hint" style={{ fontSize: 11 }}>{r.role}</small>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: "var(--panel-2)" }}>{r.country}</span>
                </div>
                <div style={{ color: "#10b981", marginBottom: 10, letterSpacing: 2 }}>★★★★★</div>
                <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0, color: "var(--text)" }}>"{r.text}"</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 26 }}>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 6 }}>How would you rate LimeShare?</div>
          <div style={{ display: "inline-flex", gap: 6 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => {
                  setUserRating(star);
                  setRated(true);
                }}
                style={{ background: "transparent", border: 0, cursor: "pointer", fontSize: 22, color: star <= userRating ? "var(--lime)" : "var(--border)" }}
                aria-label={`Rate ${star} stars`}
              >
                ★
              </button>
            ))}
          </div>
          <div className="hint" style={{ fontSize: 12, marginTop: 4 }}>
            {rated ? "Thank you for your rating!" : "Average rating 4.89/5 from 42,810 ratings"}
          </div>
        </div>
      </section>

      {/* 5. Interactive Accordion FAQ */}
      <section style={{ maxWidth: 820, margin: "0 auto 64px" }}>
        <h2 style={{ fontSize: "clamp(26px, 3.5vw, 36px)", fontWeight: 900, marginBottom: 22 }}>{t.faqTitle}</h2>
        {FAQ.map((item, i) => {
          const isOpen = openFaq === i;
          return (
            <div key={item.q} className="faq-accordion-item">
              <button className="faq-accordion-btn" onClick={() => setOpenFaq(isOpen ? null : i)}>
                <span>{item.q}</span>
                <ChevronDown size={18} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </button>
              {isOpen && <div className="faq-accordion-body">{item.a}</div>}
            </div>
          );
        })}
      </section>

      {/* 6. Latest Tips & News (3 Visual Cards) */}
      <section style={{ marginBottom: 64 }}>
        <h2 style={{ fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 900, margin: "0 0 6px" }}>Latest Tips &amp; Guides</h2>
        <p className="hint" style={{ margin: "0 0 22px" }}>Explore deep technical guides and workflow benchmarks.</p>

        <div className="pricing-grid-3" style={{ textAlign: "left" }}>
          {[
            {
              title: "How to Send Files Larger Than 25 MB? (Free & Fast Method)",
              desc: "Why Gmail and Outlook bounce attachments at 18.5 MB and how to share up to 5 GB with zero registration.",
              to: "/send-over-25-mb",
            },
            {
              title: "Transfer Files from Phone to Computer: Fastest Wireless Methods (2026)",
              desc: "Move 4K ProRes videos and photo sets between iPhone, Android, Windows, and Mac over local Wi-Fi.",
              to: "/from-iphone-to-pc",
            },
            {
              title: "The Ultimate Guide to Large File Transfer: Protocols, Security & Egress",
              desc: "4,200-word architectural breakdown of STREAM AEAD cryptography, TCP window scaling, and zero-egress storage.",
              to: "/ultimate-file-transfer-guide",
            },
          ].map((card) => (
            <Link key={card.to} to={card.to} className="panel" style={{ padding: 0, overflow: "hidden", textDecoration: "none", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "24px 22px", background: "var(--panel-2)", borderBottom: "1px solid var(--border)", color: "var(--text)", minHeight: 120, display: "flex", alignItems: "flex-end" }}>
                <b style={{ fontSize: 17, lineHeight: 1.3 }}>{card.title}</b>
              </div>
              <div style={{ padding: 20, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <p className="hint" style={{ margin: "0 0 14px", fontSize: 13 }}>{card.desc}</p>
                <span style={{ color: "var(--lime)", fontWeight: 700, fontSize: 13 }}>{t.learnMore} →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 7. Visual Head-to-Head Comparison (WeTransfer 3GB vs SendGB 5GB vs LimeShare 5GB Zero-Knowledge) */}
      <section className="panel legal" style={{ marginBottom: 24 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h2 style={{ fontSize: "clamp(24px, 3.2vw, 34px)", fontWeight: 900, margin: "0 0 6px" }}>
            {t.compareTitle}
          </h2>
          <p className="hint" style={{ margin: 0 }}>{t.compareSub}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 26 }}>
          <div style={{ padding: 24, borderRadius: 16, background: "var(--panel-2)", border: "1px solid var(--border)", textAlign: "center" }}>
            <span className="badge" style={{ background: "transparent", color: "var(--muted)", borderColor: "var(--border)" }}>WeTransfer</span>
            <div style={{ fontSize: 40, fontWeight: 900, color: "var(--muted)", margin: "10px 0 4px" }}>3 GB</div>
            <small className="hint">Capped at 10 transfers/mo · Server reads files</small>
          </div>

          <div style={{ padding: 24, borderRadius: 16, background: "var(--panel-2)", border: "1px solid var(--border)", textAlign: "center" }}>
            <span className="badge" style={{ background: "transparent", color: "var(--muted)", borderColor: "var(--border)" }}>SendGB</span>
            <div style={{ fontSize: 40, fontWeight: 900, color: "var(--text)", margin: "10px 0 4px" }}>5 GB</div>
            <small className="hint">Server-side encryption (Provider holds keys)</small>
          </div>

          <div style={{ padding: 24, borderRadius: 16, background: "var(--lime)", color: "var(--btn-text)", textAlign: "center", boxShadow: "0 12px 30px var(--lime-glow)" }}>
            <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 999, background: "rgba(0,0,0,0.2)", fontWeight: 800, fontSize: 12 }}>LimeShare</span>
            <div style={{ fontSize: 40, fontWeight: 900, margin: "10px 0 4px" }}>5 GB Free</div>
            <small style={{ opacity: 0.9, fontWeight: 600 }}>True Browser Zero-Knowledge + P2P + 30d Smart Retention</small>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Service</th>
              <th>Free Size Limit</th>
              <th>Encryption Architecture</th>
              <th>Direct P2P Mode</th>
              <th>Recipient Timer Rescue</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>WeTransfer</td>
              <td>3 GB (10/month cap)</td>
              <td>Server-side (Scans files)</td>
              <td>No</td>
              <td>No</td>
            </tr>
            <tr>
              <td>SendGB</td>
              <td>5 GB (Unlimited)</td>
              <td>Server-side (Holds keys)</td>
              <td>No</td>
              <td>Sender only</td>
            </tr>
            <tr>
              <td style={{ color: "var(--lime)", fontWeight: 800 }}>LimeShare</td>
              <td style={{ color: "var(--lime)", fontWeight: 800 }}>5 GB (Unlimited + Folders)</td>
              <td style={{ color: "var(--lime)", fontWeight: 800 }}>Client-Side Zero-Knowledge (AES-256-GCM)</td>
              <td style={{ color: "var(--lime)", fontWeight: 800 }}>Yes (LimeDrop WebRTC)</td>
              <td style={{ color: "var(--lime)", fontWeight: 800 }}>Yes (Sender &amp; Recipient)</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
